import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  CircularProgress,
  alpha,
  useTheme,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import type { Template } from "./types";

interface DeleteConfirmDialogProps {
  open: boolean;
  template: Template | null;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  open,
  template,
  isDeleting,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={() => !isDeleting && onClose()}
      maxWidth="xs"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          },
          elevation: 0,
        },
        backdrop: {
          sx: {
            bgcolor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: alpha(theme.palette.error.main, 0.12),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 2.5,
            boxShadow: `0 4px 14px ${alpha(theme.palette.error.main, 0.3)}`,
          }}
        >
          <DeleteIcon sx={{ fontSize: 32, color: "error.main" }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Delete Template
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.6 }}
        >
          Are you sure you want to delete &ldquo;
          {template?.fileName || template?.name || "this template"}
          &rdquo;? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ flexDirection: "column", gap: 1.5, px: 4, pb: 4, pt: 1 }}
      >
        <Button
          variant="contained"
          color="error"
          fullWidth
          disableElevation
          onClick={onConfirm}
          disabled={isDeleting}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            py: 1.25,
            fontWeight: 600,
          }}
          autoFocus
        >
          {isDeleting ? (
            <CircularProgress size={22} color="inherit" />
          ) : (
            "Delete Template"
          )}
        </Button>
        <Button
          variant="text"
          color="inherit"
          fullWidth
          onClick={onClose}
          disabled={isDeleting}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            py: 1.25,
            fontWeight: 500,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmDialog;
